"use client";

import type { AudioSample } from "@mw/shared/payload-types";

import { songColorToThumbnailImage } from "@/app/(pages)/[locale]/(full-screen)/music/songs/[slug]/colorToImage";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { AudioListProvider } from "@/app/_providers/audio-list";
import { toast } from "@/app/_providers/toast";
import { appUrls } from "@/constants";
import { env } from "@/env";
import { Button } from "@ui/components/core/Button";
import { Image } from "@ui/components/core/Image";
import { Skeleton } from "@ui/components/core/Skeleton";
import CoverArt from "@ui/components/routes/music/CoverArt";
import { MusicColorScheme } from "@ui/components/routes/music/colorScheme";
import { useWavesurfer } from "@ui/hooks/useWavesurferPlayer";
import { ImageSizes } from "@ui/lib/image";
import { cn } from "@ui/lib/utils";
import { filterEntitiesArray, getAudioUrl } from "@utilities/payload";
import { PauseIcon, PlayIcon, RefreshCcwIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { DemoPropsV2 } from "../BlockDemosV2";

import { LimitReachedOverlay } from "../LimitReachedOverlay";
import { OmniboxComponentWrapper } from "../OmniboxComponentWrapper";
import { type SampleOption, SamplePicker } from "../SamplePicker";
import { LinkTracked } from "@ui/components/core/LinkTracked";
import { OmniboxSimpleWrapper } from "../OmniboxSimpleWrapper";

interface MusicGenerationResponse {
  download_url: string;
  generation_settings: {
    lyrics: {
      positive_global_styles: string[];
    };
    prompt: string;
  };
  id: string;
  metadata: {
    description: string;
    title: string;
  };
}

function PromptInput({
  prompt,
  onPromptChange,
  onGenerate,
  placeholder,
  variant,
}: {
  onGenerate: () => void;
  onPromptChange: (prompt: string) => void;
  placeholder: null | string | undefined;
  prompt: string;
  variant: "default" | "simple";
}) {
  const t = useTranslations("ClientKeys.music_demo");

  return (
    <div className='flex w-full flex-col p-12 md:p-28'>
      {variant === "default" && <p className='f-ui-03 mb-12 uppercase text-light'>{t("generate_a_song")}</p>}
      <textarea
        className='scrollbar-none f-paragraph-02 w-full flex-grow resize-none border-0 focus:outline-none'
        maxLength={500}
        onChange={e => onPromptChange(e.target.value)}
        placeholder={placeholder || t("prompt_placeholder")}
        value={prompt}
      />

      <Button className='mt-48' disabled={!prompt.trim()} onClick={onGenerate} size='md' variant='primary'>
        {t("generate")}
      </Button>
    </div>
  );
}

function Examples({
  onSelect,
  examples,
  onShowMobileEditor,
  isPlaying,
  audioMetadata,
}: {
  audioMetadata: AudioMetadata | null;
  examples: AudioMetadata[];
  isPlaying: boolean;
  onSelect: (example: AudioMetadata) => void;
  onShowMobileEditor: () => void;
}) {
  const t = useTranslations("ClientKeys.music_demo");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [visibleOverlays, setVisibleOverlays] = useState<{ bottom: boolean; top: boolean }>({
    bottom: true,
    top: false,
  });

  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
          setVisibleOverlays({ bottom: scrollTop < scrollHeight - clientHeight - 10, top: scrollTop > 0 });
        }
      });
    }
  }, []);

  useEffect(() => {
    handleScroll();

    // Add scroll event listener
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);

      // Add window resize listener
      window.addEventListener("resize", handleScroll);

      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleScroll);
      };
    }
  }, [handleScroll, examples]);

  return (
    <div className='flex h-full flex-col p-12 md:p-28'>
      <p className='f-ui-03 mb-12 hidden uppercase text-light md:block'>{t("our_picks")}</p>
      <p className='f-ui-03 mb-12 uppercase text-light md:hidden'>{t("ai_generated_songs")}</p>
      <div className='relative flex min-h-0 flex-1 flex-col'>
        <div className='scrollbar-none min-h-0 flex-1 overflow-y-auto' ref={scrollContainerRef}>
          {examples.map(example => (
            <button
              className='group flex w-full items-center justify-between gap-12 rounded-md border-0 p-12 text-left hover:bg-dark'
              key={example.audioUrl}
              onClick={() => onSelect(example)}
            >
              <div className='flex gap-12'>
                <Image
                  alt={example.title}
                  className='h-40 w-40 rounded-md'
                  height={960}
                  sizes='42px'
                  src={songColorToThumbnailImage[example.colorScheme]}
                  width={960}
                />
                <div className='flex flex-col'>
                  <p className='f-paragraph-03 mb-2'>{example.title}</p>
                  <p className='f-paragraph-04 text-light'>{example.styles.join(", ")}</p>
                </div>
              </div>
              <div className='flex h-32 w-32 items-center justify-center rounded-full p-4 text-default opacity-0 group-hover:opacity-100'>
                {isPlaying && example.audioUrl === audioMetadata?.audioUrl ? (
                  <PauseIcon fill='currentColor' size={16} />
                ) : (
                  <PlayIcon fill='currentColor' size={16} />
                )}
              </div>
            </button>
          ))}
        </div>
        <div
          className={cn(
            "pointer-events-none absolute top-0 z-3 h-80 w-full bg-gradient-to-b from-[white] to-transparent transition-opacity duration-200",
            visibleOverlays.top ? "opacity-100" : "opacity-0",
          )}
        />
        <div
          className={cn(
            "pointer-events-none absolute bottom-0 z-3 h-80 w-full bg-gradient-to-t from-[white] to-transparent transition-opacity duration-200",
            visibleOverlays.bottom ? "opacity-100" : "opacity-0",
          )}
        />
      </div>
      <div className='flex justify-end pt-8 md:hidden'>
        <Button onClick={onShowMobileEditor} variant='secondary'>
          {t("create_a_song")}
        </Button>
      </div>
    </div>
  );
}

function AudioPlayer({
  audioMetadata,
  handlePlayPause,
  isPlaying,
  mediaElement,
  timelineRef,
  keyboardContainerRef,
}: {
  audioMetadata: AudioMetadata | null;
  handlePlayPause: () => void;
  isPlaying: boolean;
  keyboardContainerRef: React.RefObject<HTMLDivElement | null>;
  mediaElement: HTMLMediaElement | null;
  timelineRef: React.RefObject<HTMLDivElement | null>;
}) {
  return (
    <div ref={keyboardContainerRef} className='flex h-full flex-col justify-center p-12 md:p-28' tabIndex={-1}>
      <CoverArt
        audioElement={mediaElement}
        className='mb-32'
        colorScheme={audioMetadata?.colorScheme || MusicColorScheme.red}
        hidePlayButton
        onClick={handlePlayPause}
        songId={audioMetadata?.audioUrl}
        styles={audioMetadata?.styles}
        title={audioMetadata?.title}
      />
      <div className='flex flex-col gap-16'>
        <div className='flex items-center gap-16'>
          <Button onClick={handlePlayPause} size='pill' variant='secondary'>
            {isPlaying ? <PauseIcon fill='currentColor' size={16} /> : <PlayIcon fill='currentColor' size={16} />}
          </Button>
          <div className='wavesurfer h-32 flex-1 cursor-pointer' ref={timelineRef} />
        </div>
      </div>
    </div>
  );
}

function GeneratedSong({
  activeAudio,
  audioMetadata,
  isGenerating,
  prompt,
  songId,
  handlePlayPause,
  handleRestart,
  isPlaying,
  variant,
}: {
  activeAudio: AudioMetadata | null;
  audioMetadata: AudioMetadata | null;
  handlePlayPause: () => void;
  handleRestart: () => void;
  isGenerating: boolean;
  isPlaying: boolean;
  prompt: string;
  songId: null | string | undefined;
  variant: "default" | "simple";
}) {
  const t = useTranslations("ClientKeys.music_demo");

  const continueLink = useMemo(() => {
    if (!songId) return appUrls.music;

    return `${appUrls.music}?claimSongId=${songId}`;
  }, [songId]);

  return (
    <div className='scrollbar-none relative flex h-full w-full flex-col overflow-y-auto p-12 md:p-28'>
      <p className='f-ui-03 mb-12 uppercase text-light'>{isGenerating ? t("generating") : t("your_song_is_ready")}</p>
      <div className='mb-28 flex flex-1 items-center gap-16'>
        {isGenerating ? (
          <>
            <Skeleton className='h-80 w-80 rounded-md' />
            <div className='flex flex-1 flex-col justify-center'>
              <Skeleton className='mb-4 h-24 w-1/3' />
              <p className='animate-opacity f-paragraph-02 text-light'>“{prompt}”</p>
            </div>
          </>
        ) : audioMetadata ? (
          <>
            <Image
              alt={audioMetadata.title}
              className='h-80 w-80 rounded-md'
              height={960}
              sizes={ImageSizes.INDUSTRY_CARDS}
              src={songColorToThumbnailImage[audioMetadata.colorScheme]}
              width={960}
            />
            <div className='flex flex-1 flex-col justify-center'>
              <p className='f-paragraph-01 mb-4'>{audioMetadata.title}</p>
              <p className='f-paragraph-02 text-light'>“{prompt}”</p>
            </div>
          </>
        ) : null}
      </div>
      <div className='flex gap-8'>
        {isGenerating ? (
          <Button disabled={isGenerating} onClick={handlePlayPause} size='md' variant='primary'>
            {t("generating")}
          </Button>
        ) : (
          <>
            <Button onClick={handlePlayPause} size='md' variant='primary'>
              {isPlaying && activeAudio?.audioUrl === audioMetadata?.audioUrl ? "Pause" : "Play"}
            </Button>
            <LinkTracked
              asButton
              buttonVariants={{ variant: "secondary", size: "md" }}
              eventName={EventName.marketing_website_music_demo_continue_editing}
              href={continueLink}
              rel='noopener noreferrer'
              target='_blank'
            >
              {t("signup_cta")}
            </LinkTracked>
            <Button
              className='absolute right-12 top-12 md:right-28 md:top-28'
              onClick={handleRestart}
              size='pill-sm'
              variant='secondary'
            >
              <RefreshCcwIcon size={12} />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

interface AudioMetadata {
  audioUrl: string;
  colorScheme: MusicColorScheme;
  songId?: string;
  styles: string[];
  title: string;
}

export interface MusicDemoProps extends DemoPropsV2 {
  hideSidebar?: boolean;
  musicPromptPlaceholder?: null | string;
  musicSamples?: AudioSample[] | null;
  variant?: "default" | "simple";
}

export default function MusicDemo(props: MusicDemoProps) {
  return (
    <AudioListProvider>
      <MusicDemoComponent {...props} />
    </AudioListProvider>
  );
}

function Wrapper({ children, variant, initiallyVisible, size }: MusicDemoProps & { children: React.ReactNode }) {
  if (variant === "simple") {
    return <OmniboxSimpleWrapper className='h-[21.25rem] md:h-[23.5rem]'>{children}</OmniboxSimpleWrapper>;
  }

  return (
    <OmniboxComponentWrapper component='music' initiallyVisible={initiallyVisible} size={size}>
      {children}
    </OmniboxComponentWrapper>
  );
}

function MusicDemoComponent({
  musicSamples,
  musicPromptPlaceholder,
  variant = "default",
  hideSidebar = false,
  ...props
}: MusicDemoProps) {
  const t = useTranslations("ClientKeys.music_demo");
  const examples: AudioMetadata[] = useMemo(() => {
    return filterEntitiesArray(musicSamples)
      .map((sample, index) => ({
        audioUrl: getAudioUrl(sample.audio) || "",
        colorScheme: (sample.orbColor as MusicColorScheme) || MusicColorScheme.red,
        styles:
          sample.styleTags?.map(tag => tag.tag || "").map(tag => `${tag.charAt(0).toUpperCase()}${tag.slice(1)}`) || [],
        title: sample.title || `Sample ${index + 1}`,
      }))
      .filter(sample => sample.audioUrl !== "");
  }, [musicSamples]);

  const samplePickerItems = useMemo(() => {
    return examples.map(example => ({
      label: example.title,
      value: example.audioUrl,
    }));
  }, [examples]);

  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [showLimitReachedOverlay, setShowLimitReachedOverlay] = useState(false);
  const [activeAudio, setActiveAudio] = useState<AudioMetadata | null>(examples[0]);
  const [generatedSongMetadata, setGeneratedSongMetadata] = useState<AudioMetadata | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const [showMobileEditor, setShowMobileEditor] = useState(variant !== "default");

  const { isPlaying, play, togglePlayPause, mediaElement, duration, keyboardContainerRef } = useWavesurfer({
    url: activeAudio?.audioUrl || "",
    timelineRef,
    id: activeAudio?.audioUrl || "default",
    height: 32,
    waveColor: "lightgray",
    progressColor: "#030712",
  });

  const handleGenerate = async () => {
    if (!prompt.trim()) return;

    setIsGenerating(true);
    captureEvent(EventName.marketing_website_music_demo_generate, {
      prompt,
      source: "music_demo",
    });

    try {
      const response = await fetch(`${env.NEXT_PUBLIC_XI_SERVER_URL}/v1/music/detailed/anonymous`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ prompt }),
      });

      if (!response.ok) {
        if (typeof response === "object" && response !== null && "status" in response) {
          const parsedResponse = await response.json();
          const errorStatus = parsedResponse.detail.status;
          if (errorStatus === "quota_exceeded") {
            setShowLimitReachedOverlay(true);
            captureEvent(EventName.marketing_website_music_demo_quota_exceeded, {
              prompt,
              source: "music_demo",
            });
            return;
          } else {
            toast.error(`Failed to generate music: ${parsedResponse.detail.message}`);
            captureEvent(EventName.marketing_website_music_demo_error, {
              prompt,
              error: parsedResponse.detail.message || "Unknown error",
              source: "music_demo",
            });
            return;
          }
        }
        throw new Error("Failed to generate music: unknown error");
      }

      const data = (await response.json()) as MusicGenerationResponse;
      const metadata = {
        songId: data.id,
        styles: data.generation_settings.lyrics.positive_global_styles,
        title: data.metadata.title,
        audioUrl: data.download_url,
        colorScheme: MusicColorScheme.red,
      };
      setActiveAudio(metadata);
      setGeneratedSongMetadata(metadata);
      captureEvent(EventName.mw_music_category_generation_successful, {
        prompt,
        source: "music_demo",
      });
    } catch (error) {
      toast.error("Failed to generate music");
      captureEvent(EventName.marketing_website_music_demo_error, {
        prompt,
        error: error instanceof Error ? error.message : "Unknown error",
        source: "music_demo",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePlayPause = () => {
    if (!isPlaying) {
      const index = examples.findIndex(example => example.audioUrl === activeAudio?.audioUrl);
      captureEvent(EventName.marketing_website_music_demo_play_sample, {
        type: index === -1 ? "example" : "generated",
        index,
      });
    }
    togglePlayPause();
  };

  const handleGeneratedSongPlayPause = () => {
    if (activeAudio?.audioUrl !== generatedSongMetadata?.audioUrl) {
      captureEvent(EventName.mw_music_category_generation_played, { source: "music_demo" });
      setActiveAudio(generatedSongMetadata);
      play(generatedSongMetadata?.audioUrl);
      return;
    }

    setTimeout(() => {
      handlePlayPause();
    }, 0);
  };

  const handleSamplePickerSelected = (sample: SampleOption) => {
    const example = examples.find(example => example.audioUrl === sample.value);
    if (example) {
      handleSelectExample(example);
    }
  };

  const handleSelectExample = (example: AudioMetadata) => {
    if (example.audioUrl === activeAudio?.audioUrl && isPlaying) {
      togglePlayPause();
      return;
    }

    setActiveAudio(example);

    setTimeout(() => {
      play(example.audioUrl);
    }, 0);
  };

  const handleRestart = () => {
    setActiveAudio(examples[0]);
    setGeneratedSongMetadata(null);
    setPrompt("");
  };

  return (
    <>
      <Wrapper variant={variant} initiallyVisible={props.initiallyVisible} size={props.size}>
        <div className={cn("flex h-full w-full flex-col", variant === "default" && "bg-dark p-4")}>
          <div
            className={cn("relative flex flex-1 overflow-hidden", variant !== "simple" && "rounded-inxl bg-default")}
          >
            {showLimitReachedOverlay && (
              <LimitReachedOverlay description={t("quota_exceeded_description")} title={t("quota_exceeded_title")} />
            )}
            <div
              className={cn(
                "flex h-full flex-shrink-0 basis-full flex-col border-default md:basis-2/3 md:border-r",
                hideSidebar && "md:basis-full",
              )}
            >
              <div
                className={cn(
                  "scrollbar-none hidden w-full flex-1 overflow-y-auto md:flex",
                  showMobileEditor && "flex",
                  variant === "default" && "border-b border-default",
                )}
              >
                {!generatedSongMetadata && !isGenerating ? (
                  <PromptInput
                    onGenerate={handleGenerate}
                    onPromptChange={setPrompt}
                    placeholder={musicPromptPlaceholder}
                    prompt={prompt}
                    variant={variant}
                  />
                ) : (
                  <GeneratedSong
                    activeAudio={activeAudio}
                    audioMetadata={generatedSongMetadata}
                    handlePlayPause={handleGeneratedSongPlayPause}
                    handleRestart={handleRestart}
                    isGenerating={isGenerating}
                    isPlaying={isPlaying}
                    prompt={prompt}
                    songId={generatedSongMetadata?.songId}
                    variant={variant}
                  />
                )}
              </div>
              {!showMobileEditor && examples.length > 0 && variant === "default" && (
                <div className='flex-1 overflow-hidden'>
                  <Examples
                    audioMetadata={activeAudio}
                    examples={examples}
                    isPlaying={isPlaying}
                    onSelect={handleSelectExample}
                    onShowMobileEditor={() => setShowMobileEditor(true)}
                  />
                </div>
              )}
            </div>
            <div className={cn("hidden flex-grow-0 flex-col md:flex md:basis-1/3", hideSidebar && "md:hidden")}>
              <AudioPlayer
                audioMetadata={activeAudio}
                handlePlayPause={handlePlayPause}
                isPlaying={isPlaying}
                keyboardContainerRef={keyboardContainerRef}
                mediaElement={mediaElement}
                timelineRef={timelineRef}
              />
            </div>
          </div>
        </div>
      </Wrapper>
      {variant === "simple" && (
        <div className='mt-8'>
          <SamplePicker onSelected={handleSamplePickerSelected} samples={samplePickerItems} />
        </div>
      )}
    </>
  );
}
